import { useState } from 'react';

import type { ReactNode } from 'react';

import {
  Box,
  Button,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import OpenInNewRoundedIcon
  from '@mui/icons-material/OpenInNewRounded';

import CustomerPortfolioDetailModal
  from './CustomerPortfolioDetailModal';

import type {
  MarginCenterComparisonMode,
  MarginCenterCustomerPortfolioAnalysis,
  MarginCenterCustomerPortfolioMixHighlight,
  MarginCenterCustomerPortfolioParetoPeriod,
} from '../types/marginCenter';

import type {
  MarginCenterCustomerPortfolioDetailType,
} from '../types/customerPortfolioDetail';
import { pricingNumberFormat } from '../../../modules/pricingJourney/formatNumber';


interface CustomerPortfolioAnalysisPanelProps {
  analysis: MarginCenterCustomerPortfolioAnalysis;

  customerId: string;

  startDate: string | null;
  endDate: string | null;

  comparisonMode: MarginCenterComparisonMode;
  comparisonStartDate: string | null;
  comparisonEndDate: string | null;

  currencyCode: string | null;
  hasComparison: boolean;

  onOpenRelationship: (productId: string) => void;
}


const numberFormatter = pricingNumberFormat({
  maximumFractionDigits: 2,
});

const integerFormatter = pricingNumberFormat({
  maximumFractionDigits: 0,
});


function formatNumber(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  return numberFormatter.format(value);
}


function formatInteger(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  return integerFormatter.format(value);
}


function formatPct(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  return `${formatNumber(value)} %`;
}


function formatSignedPoints(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  return `${value > 0 ? '+' : ''}${formatNumber(value)} pp`;
}


function formatCurrency(
  value: number | null | undefined,
  currencyCode: string | null,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  if (!currencyCode) {
    return formatNumber(value);
  }

  try {
    return pricingNumberFormat({
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return formatNumber(value);
  }
}


function movementColor(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined || value === 0) {
    return '#94a3b8';
  }

  return value > 0
    ? '#34d399'
    : '#fb7185';
}


function AnalysisCard({
  title,
  eyebrow,
  action,
  children,
}: {
  title: string;
  eyebrow: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        borderRadius: 2.5,
        border:
          '1px solid rgba(148,163,184,0.14)',
        bgcolor:
          'rgba(2,6,23,0.24)',
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="overline"
            sx={{
              color: '#64748b',
              fontSize: '0.66rem',
              fontWeight: 850,
              letterSpacing: 0.8,
              lineHeight: 1.3,
            }}
          >
            {eyebrow}
          </Typography>

          <Typography
            variant="subtitle2"
            sx={{
              mt: 0.15,
              color: '#f8fafc',
              fontWeight: 850,
            }}
          >
            {title}
          </Typography>
        </Box>

        {action && (
          <Box
            sx={{
              flexShrink: 0,
              pt: 0.1,
            }}
          >
            {action}
          </Box>
        )}
      </Box>

      <Box sx={{ mt: 1 }}>
        {children}
      </Box>
    </Paper>
  );
}


function ParetoReading({
  period,
  label,
}: {
  period: MarginCenterCustomerPortfolioParetoPeriod | null;
  label: string;
}) {
  if (!period) {
    return null;
  }

  if (period.status !== 'ok') {
    return (
      <Typography
        variant="caption"
        sx={{
          color: '#64748b',
          lineHeight: 1.45,
        }}
      >
        {label}: concentración no calculable con la evidencia disponible.
      </Typography>
    );
  }

  return (
    <Typography
      variant="caption"
      sx={{
        color: '#94a3b8',
        lineHeight: 1.45,
      }}
    >
      {label}: {formatInteger(
        period.products_for_80_pct_revenue,
      )} productos, {formatPct(
        period.products_for_80_pct_portfolio_share_pct,
      )} de la cartera.
    </Typography>
  );
}


function MixRelationship({
  item,
  onOpenRelationship,
}: {
  item: MarginCenterCustomerPortfolioMixHighlight;
  onOpenRelationship: (productId: string) => void;
}) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns:
          'minmax(0, 1fr) auto auto',
        gap: 0.8,
        alignItems: 'center',
        py: 0.55,
        borderBottom:
          '1px solid rgba(148,163,184,0.08)',

        '&:last-of-type': {
          borderBottom: 'none',
        },
      }}
    >
      <Typography
        variant="caption"
        noWrap
        sx={{
          color: '#cbd5e1',
          fontWeight: 750,
        }}
      >
        {item.label}
      </Typography>

      <Typography
        variant="caption"
        sx={{
          color: movementColor(
            item.revenue_share_delta_points,
          ),
          fontWeight: 850,
          whiteSpace: 'nowrap',
        }}
      >
        {formatSignedPoints(
          item.revenue_share_delta_points,
        )}
      </Typography>

      <Button
        size="small"
        variant="text"
        endIcon={
          <OpenInNewRoundedIcon
            sx={{ fontSize: 14 }}
          />
        }
        onClick={() =>
          onOpenRelationship(item.product_id)
        }
        sx={{
          minWidth: 0,
          px: 0.5,
          fontSize: '0.7rem',
          textTransform: 'none',
        }}
      >
        Examinar
      </Button>
    </Box>
  );
}


export default function CustomerPortfolioAnalysisPanel({
  analysis,
  customerId,
  startDate,
  endDate,
  comparisonMode,
  comparisonStartDate,
  comparisonEndDate,
  currencyCode,
  hasComparison,
  onOpenRelationship,
}: CustomerPortfolioAnalysisPanelProps) {
  const [
    detailModal,
    setDetailModal,
  ] = useState<
    MarginCenterCustomerPortfolioDetailType
    | null
  >(null);

  const concentration =
    analysis.concentration.revenue;

  const current =
    concentration.current;

  const comparison =
    concentration.comparison;

  const change =
    concentration.change;

  const core =
    analysis.core_movement;

  const mix =
    analysis.mix_movement;

  const rotation =
    analysis.activity_rotation;

  const topGains =
    mix.largest_share_gains.slice(0, 3);

  const topLosses =
    mix.largest_share_losses.slice(0, 3);

  const paretoMet =
    current.pareto_80_20_met;

  const concentrationChange =
    change?.products_for_80_pct_revenue_delta
    ?? null;

  return (
    <Box sx={{ mt: 2 }}>
      <Stack spacing={0.35}>
        <Typography
          variant="subtitle1"
          sx={{
            color: '#f8fafc',
            fontWeight: 900,
          }}
        >
          Estructura de la cartera
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: '#94a3b8',
            lineHeight: 1.5,
          }}
        >
          Cómo se concentra la facturación, qué productos ganan o pierden peso y cómo cambia el núcleo de la cartera.
        </Typography>
      </Stack>

      <Box
        sx={{
          mt: 1.5,
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            xl: 'repeat(2, minmax(0, 1fr))',
          },
          gap: 1,
        }}
      >
        <AnalysisCard
          eyebrow="Concentración"
          title="Qué parte de la cartera explica los ingresos"
          action={
            current.status === 'ok' ? (
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  setDetailModal('concentration')
                }
                sx={{
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                Ver desglose
              </Button>
            ) : null
          }
        >
          {current.status === 'ok' ? (
            <Stack spacing={0.75}>
              <Typography
                variant="h6"
                sx={{
                  color: '#f8fafc',
                  fontWeight: 900,
                }}
              >
                {formatInteger(
                  current.products_for_80_pct_revenue,
                )} de {formatInteger(
                  current.active_products,
                )} productos → 80 % de los ingresos
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: '#94a3b8',
                  lineHeight: 1.5,
                }}
              >
                Hace falta el {formatPct(
                  current.products_for_80_pct_portfolio_share_pct,
                )} de la cartera activa para alcanzar el 80 % de la facturación.
              </Typography>

              <Box
                sx={{
                  height: 7,
                  borderRadius: 99,
                  bgcolor:
                    'rgba(148,163,184,0.12)',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                    width: `${Math.min(
                      100,
                      Math.max(
                        0,
                        current.top_20_pct_revenue_share_pct
                        ?? 0,
                      ),
                    )}%`,
                    bgcolor: '#38bdf8',
                    borderRadius: 99,
                  }}
                />
              </Box>

              <Typography
                variant="caption"
                sx={{
                  color: '#94a3b8',
                }}
              >
                El 20 % de los productos genera el {formatPct(
                  current.top_20_pct_revenue_share_pct,
                )} de los ingresos.
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  color:
                    paretoMet === true
                      ? '#38bdf8'
                      : '#64748b',
                  fontWeight: 750,
                }}
              >
                {paretoMet === true
                  ? 'En este periodo sí se observa una concentración 80/20.'
                  : 'En este periodo no se observa una concentración 80/20.'}
              </Typography>

              {hasComparison && comparison && (
                <ParetoReading
                  period={comparison}
                  label="Periodo comparado"
                />
              )}

              {hasComparison
                && concentrationChange !== null && (
                <Typography
                  variant="caption"
                  sx={{
                    color: movementColor(
                      -concentrationChange,
                    ),
                    fontWeight: 750,
                  }}
                >
                  {concentrationChange === 0
                    ? 'El número de productos necesario para alcanzar el 80 % no cambia.'
                    : concentrationChange > 0
                      ? `Ahora hacen falta ${formatInteger(
                          concentrationChange,
                        )} productos más para alcanzar el 80 %.`
                      : `Ahora hacen falta ${formatInteger(
                          Math.abs(
                            concentrationChange,
                          ),
                        )} productos menos para alcanzar el 80 %.`}
                </Typography>
              )}
            </Stack>
          ) : (
            <Typography
              variant="body2"
              sx={{
                color: '#94a3b8',
                lineHeight: 1.5,
              }}
            >
              La concentración estándar 80/20 no es calculable en este periodo. Esto puede ocurrir cuando existen importes netos negativos por producto o la facturación neta total no es positiva.
            </Typography>
          )}
        </AnalysisCard>

        <AnalysisCard
          eyebrow="Núcleo"
          title="Cómo cambia el grupo que sostiene el 80 %"
          action={
            core.status === 'ok' ? (
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  setDetailModal('core_movement')
                }
                sx={{
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                Ver cambios del núcleo
              </Button>
            ) : null
          }
        >
          {core.status === 'ok' ? (
            <Stack spacing={1}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns:
                    'repeat(3, minmax(0, 1fr))',
                  gap: 0.75,
                }}
              >
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#f8fafc',
                      fontWeight: 900,
                    }}
                  >
                    {formatInteger(
                      core.stayed_in_core,
                    )}
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{ color: '#64748b' }}
                  >
                    permanecen
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#34d399',
                      fontWeight: 900,
                    }}
                  >
                    {formatInteger(
                      core.joined_current_core,
                    )}
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{ color: '#64748b' }}
                  >
                    entran en el núcleo
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#fb7185',
                      fontWeight: 900,
                    }}
                  >
                    {formatInteger(
                      core.left_current_core,
                    )}
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{ color: '#64748b' }}
                  >
                    salen del núcleo
                  </Typography>
                </Box>
              </Box>

              <Typography
                variant="body2"
                sx={{
                  color: '#94a3b8',
                  lineHeight: 1.5,
                }}
              >
                El núcleo es el conjunto mínimo de productos que acumula el 80 % de los ingresos. Entrar o salir de él no significa que un producto sea nuevo o perdido.
              </Typography>

            </Stack>
          ) : (
            <Typography
              variant="body2"
              sx={{
                color: '#94a3b8',
                lineHeight: 1.5,
              }}
            >
              El cambio de núcleo necesita una comparación y una concentración calculable en ambos periodos.
            </Typography>
          )}
        </AnalysisCard>

        <AnalysisCard
          eyebrow="Mix"
          title="Qué productos ganan y pierden peso"
          action={
            mix.status === 'ok' ? (
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  setDetailModal('mix_movement')
                }
                sx={{
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                Ver cambios de mix
              </Button>
            ) : null
          }
        >
          {mix.status === 'ok' ? (
            <Stack spacing={1}>
              <Stack
                direction="row"
                spacing={2}
                useFlexGap
                flexWrap="wrap"
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: '#34d399',
                    fontWeight: 850,
                  }}
                >
                  {formatInteger(
                    mix.gaining_share_products,
                  )} ganan peso
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: '#fb7185',
                    fontWeight: 850,
                  }}
                >
                  {formatInteger(
                    mix.losing_share_products,
                  )} pierden peso
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: '#94a3b8',
                    fontWeight: 750,
                  }}
                >
                  {formatInteger(
                    mix.continued_products,
                  )} comparables
                </Typography>
              </Stack>

              {(topGains.length > 0
                || topLosses.length > 0) && (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      md:
                        'repeat(2, minmax(0, 1fr))',
                    },
                    rowGap: 1,
                    columnGap: {
                      xs: 1,
                      md: 3,
                    },
                  }}
                >
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#64748b',
                        fontWeight: 800,
                      }}
                    >
                      Mayor ganancia de peso
                    </Typography>

                    {topGains.map((item) => (
                      <MixRelationship
                        key={item.product_id}
                        item={item}
                        onOpenRelationship={
                          onOpenRelationship
                        }
                      />
                    ))}
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#64748b',
                        fontWeight: 800,
                      }}
                    >
                      Mayor pérdida de peso
                    </Typography>

                    {topLosses.map((item) => (
                      <MixRelationship
                        key={item.product_id}
                        item={item}
                        onOpenRelationship={
                          onOpenRelationship
                        }
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Stack>
          ) : (
            <Typography
              variant="body2"
              sx={{
                color: '#94a3b8',
                lineHeight: 1.5,
              }}
            >
              El cambio de mix requiere actividad comparable en ambos periodos.
            </Typography>
          )}
        </AnalysisCard>

        <AnalysisCard
          eyebrow="Rotación"
          title="Actividad que aparece y desaparece"
          action={
            rotation.status === 'ok' ? (
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  setDetailModal('activity_rotation')
                }
                sx={{
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                Ver rotación
              </Button>
            ) : null
          }
        >
          {rotation.status === 'ok' ? (
            <Stack spacing={0.9}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns:
                    'repeat(2, minmax(0, 1fr))',
                  gap: 1,
                }}
              >
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#34d399',
                      fontWeight: 900,
                    }}
                  >
                    {formatInteger(
                      rotation.entered_current_products,
                    )}
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{
                      color: '#94a3b8',
                    }}
                  >
                    solo actuales · {formatCurrency(
                      rotation.entered_current_revenue,
                      currencyCode,
                    )} · {formatPct(
                      rotation.entered_current_revenue_share_pct,
                    )} de ingresos actuales
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#fb7185',
                      fontWeight: 900,
                    }}
                  >
                    {formatInteger(
                      rotation.missing_current_products,
                    )}
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{
                      color: '#94a3b8',
                    }}
                  >
                    solo comparados · {formatCurrency(
                      rotation.missing_comparison_revenue,
                      currencyCode,
                    )} · {formatPct(
                      rotation.missing_comparison_revenue_share_pct,
                    )} de ingresos comparados
                  </Typography>
                </Box>
              </Box>

              <Typography
                variant="body2"
                sx={{
                  color: movementColor(
                    rotation.net_active_product_change,
                  ),
                  fontWeight: 800,
                }}
              >
                Saldo de actividad: {rotation.net_active_product_change !== null
                  && rotation.net_active_product_change > 0
                  ? '+'
                  : ''}{formatInteger(
                  rotation.net_active_product_change,
                )} productos.
              </Typography>

              {rotation.revenue_replacement_ratio_pct !== null && (
                <Typography
                  variant="caption"
                  sx={{
                    color: '#94a3b8',
                    lineHeight: 1.45,
                  }}
                >
                  Los ingresos de productos solo actuales equivalen al {formatPct(
                    rotation.revenue_replacement_ratio_pct,
                  )} de los ingresos que aportaban los productos solo comparados. Es una comparación de magnitudes, no demuestra sustitución entre productos.
                </Typography>
              )}
            </Stack>
          ) : (
            <Typography
              variant="body2"
              sx={{
                color: '#94a3b8',
              }}
            >
              La rotación necesita un periodo de comparación.
            </Typography>
          )}
        </AnalysisCard>
      </Box>

      {analysis.limitations.length > 0 && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 1,
            color: '#64748b',
          }}
        >
          Parte del análisis de concentración no es calculable con la estructura neta de ingresos observada en esta selección.
        </Typography>
      )}

      <CustomerPortfolioDetailModal
        open={detailModal !== null}
        detail={
          detailModal
          ?? 'concentration'
        }
        customerId={customerId}
        startDate={startDate}
        endDate={endDate}
        comparisonMode={comparisonMode}
        comparisonStartDate={
          comparisonStartDate
        }
        comparisonEndDate={
          comparisonEndDate
        }
        currencyCode={currencyCode}
        onOpenRelationship={
          onOpenRelationship
        }
        onClose={() =>
          setDetailModal(null)
        }
      />
    </Box>
  );
}
